import { ChangeDetectionStrategy, Component, inject, effect } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { isRtlLanguage } from '@core/utils/rtl.util';
import { AppStore } from '@core/states/app/app.store';
import { HttpClient } from '@angular/common/http';
import { GqlParser } from '@unipin/angular-gql';
import { trace } from '@opentelemetry/api';

@Component({
  selector: 'ub-root',
  styleUrl: './app.component.scss',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col h-screen'
  },
  imports: [
    RouterOutlet,
  ]
})
export class AppComponent {

  public readonly appStore = inject(AppStore);
  protected readonly transloco = inject(TranslocoService);
  public readonly http = inject(HttpClient);
  public readonly gql = inject(GqlParser);

  public triggerBeError(): void {
    const tracer = trace.getTracer('mf-unibox-test');
    tracer.startActiveSpan('BE Error Test', span => {
      span.setAttribute('test.type', 'be_error');
      this.http.get('/api/invalid-endpoint').subscribe({
        next: () => span.end(),
        error: () => span.end()
      });
    });
  }

  public triggerFeError(): void {
    const tracer = trace.getTracer('mf-unibox-test');
    tracer.startActiveSpan('FE Error Test', span => {
      span.setAttribute('test.type', 'fe_error');
      const error = new Error('FE test error');
      span.recordException(error);
      span.setStatus({ code: 2, message: 'FE error occurred' });
      span.end();
      throw error;
    });
  }

  public triggerCombinedError(): void {
    const tracer = trace.getTracer('mf-unibox-test');
    tracer.startActiveSpan('Combined Error Test', span => {
      span.setAttribute('test.type', 'combined_error');
      this.transloco.load('en').subscribe(() => {
        span.setAttribute('combined.be_success', true);
        // Create sub-span for FE error
        const feTracer = trace.getTracer('mf-unibox-test');
        feTracer.startActiveSpan('FE Error Sub-span', subSpan => {
          subSpan.setAttribute('sub.type', 'fe_error');
          const error = new Error('Combined FE error after successful BE call');
          subSpan.recordException(error);
          subSpan.setStatus({ code: 2, message: 'FE error after BE success' });
          subSpan.end();
        });
        span.setStatus({ code: 2, message: 'Combined error occurred' });
        span.end();
        throw new Error('Combined FE error after successful BE call');
      });
    });
  }

  constructor() {
    effect(
      () => {
        const lang = this.transloco.getActiveLang();
        const isRtl = isRtlLanguage(lang);
        
        document.documentElement.dir = isRtl? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
      }
    );
  }
}
